import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";

interface DesktopModeContextType {
  isDesktopMode: boolean;
  toggleDesktopMode: () => void;
  enableDesktopMode: () => void;
  exitDesktopMode: () => void;
}

const DesktopModeContext = createContext<DesktopModeContextType | null>(null);

export function DesktopModeProvider({ children }: { children: ReactNode }) {
  const [isDesktopMode, setIsDesktopMode] = useState(() => {
    return localStorage.getItem("accoupro-desktop-mode") === "true";
  });

  const enterFullscreen = useCallback(() => {
    try {
      document.documentElement.requestFullscreen?.();
    } catch {}
  }, []);

  const exitFullscreen = useCallback(() => {
    try {
      if (document.fullscreenElement) document.exitFullscreen?.();
    } catch {}
  }, []);

  const enableDesktopMode = useCallback(() => {
    setIsDesktopMode(true);
    localStorage.setItem("accoupro-desktop-mode", "true");
    enterFullscreen();
  }, [enterFullscreen]);

  const exitDesktopMode = useCallback(() => {
    setIsDesktopMode(false);
    localStorage.setItem("accoupro-desktop-mode", "false");
    exitFullscreen();
  }, [exitFullscreen]);

  const toggleDesktopMode = useCallback(() => {
    if (isDesktopMode) exitDesktopMode();
    else enableDesktopMode();
  }, [isDesktopMode, enableDesktopMode, exitDesktopMode]);

  // Sync with fullscreen exit via Esc
  useEffect(() => {
    const handler = () => {
      if (!document.fullscreenElement && isDesktopMode) {
        setIsDesktopMode(false);
        localStorage.setItem("accoupro-desktop-mode", "false");
      }
    };
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, [isDesktopMode]);

  return (
    <DesktopModeContext.Provider value={{ isDesktopMode, toggleDesktopMode, enableDesktopMode, exitDesktopMode }}>
      {children}
    </DesktopModeContext.Provider>
  );
}

export function useDesktopMode() {
  const ctx = useContext(DesktopModeContext);
  if (!ctx) throw new Error("useDesktopMode must be used within DesktopModeProvider");
  return ctx;
}
